import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  TextField,
  Autocomplete,
} from "@mui/material";
import styles from "./Status.module.css";
import Input from "../../Components/Forms/Input";
import Button from "../../Components/Forms/Button";
import SelectedList from "../../Components/SelectedList/SelectedList";
import Table from "../../Components/Table/Table";
import { MdUpload } from "react-icons/md";
import { formatDateFromObject, formatFileName, optionsType, optionsOrigin } from "../../Components/Helper";
import { fetchPost } from "../../Services/Slices/postSlice";
import { useDispatch, useSelector } from "react-redux";
import { fetchGetFiles } from "../../Services/Slices/getFilesSlice";
import Snackbar from "../../Components/Snackbar/Snackbar";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import "@taak/react-modern-calendar-datepicker/lib/DatePicker.css"
import { fetchListPosts } from "../../Services/Slices/listPostsSlice";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import 'dayjs/locale/pt-br';


interface File {
  file_name: string;
  id: string;
}

const Status = () => {
  const dispatch = useDispatch();
  const post = useSelector((state: any) => state.postSlice);
  const getFiles = useSelector((state: any) => state.getFilesSlice);
  const deleteFile = useSelector((state: any) => state.deleteFileSlice);
  const listPosts = useSelector((state: any) => state.listPostsSlice);
  const [page, setPage] = useState<number>(1);
  const [showTooltip, setShowTooltip] = useState(false);
  const [isDispatched, setIsDispatched] = useState<boolean>(false);
  const [isResponsive, setIsResponsive] = useState<boolean>(false);
  const [snackbarType, setSnackbarType] = useState<string | null>(null);
  const [selectedAction, setSelectedAction] = useState<string | null>(null);
  const [revokedEditions, setRevokedEditions] = useState<string[]>([]);
  const [alteredEditions, setAlteredEditions] = useState<string[]>([]);
  const [revokeSelected, setRevokeSelected] = useState<boolean>(false);
  const [alteredSelected, setAlteredSelected] = useState<boolean>(false);
  
  const [form, setForm] = useState<any>({
    file: File,
    date: {
      year: new Date().getFullYear(),
      month: new Date().getMonth() + 1,
      day: new Date().getUTCDate(),
    },
    type: [],
    origin: [],
    status: [],
    code: "",
    title: "",
    time: "",
    editions: []
  });

  const isFormValid = (): boolean => {
    const hasCode = form.code.trim() !== "";
    const hasType = Array.isArray(form.type) && form.type.length > 0;
    const hasValidDocx = form.file instanceof File && form.file.name.endsWith(".docx");
  
    return hasCode && hasType && hasValidDocx;
  };
  
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [isSecondDialogOpen, setIsSecondDialogOpen] = useState<boolean>(false);
  const [isThirdDialogOpen, setIsThirdDialogOpen] = useState<boolean>(false);
  const columns = [
    { title: "Nome", property: "name" },
    { title: "Data", property: "date" },
    {title: "Horário", property: "hour"},
    { title: "Download", property: "presigned_url" },
    { title: "Excluir", property: "delete" },
  ];
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement> | any) => {
    const { name, value } = e.target;
    if (name === "code") {
      const numericValue = value.replace(/\D/g, "");
      setForm((prev: any) => ({
          ...prev,
          [name]: numericValue,
      }));
      return;
  }

    if (name === "date") {
      setForm((prev: any) => ({
        ...prev,
        [name]: value,
      }));
    } else {
      setForm((prev: any) => {
        if (Array.isArray(prev[name])) {
          return {
            ...prev,
            [name]: [...prev[name], value],
          };
        }
        return {
          ...prev,
          [name]: value,
        };
      });
    }
  };

  const handleTime = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    const numericValue = value.replace(/\D/g, "").toString();
    if (numericValue.length > 0 && (parseInt(numericValue[0], 10) > 2 || parseInt(numericValue[0], 10) > 2)) {
      return;
    }
    if (numericValue === "") {
      setForm((prev: any) => ({
        ...prev,
        time: "",
      }));
    }
    let formattedValue = numericValue;
    if (numericValue.length > 2 && !numericValue.includes(":")) {
      formattedValue = numericValue.slice(0, 2) + ":" + numericValue.slice(2);
    }
    if (formattedValue.length > 5) {
      formattedValue = formattedValue.substring(0, 5);
    }
    setForm((prev: any) => ({
      ...prev,
      time: formattedValue,
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    const fileExtension = file?.name.split(".").pop();
    if (fileExtension === "docx") {
      setForm((prev: any) => ({
        ...prev,
        file: file,
      }));
    } else {
      setForm((prev: any) => ({
        ...prev,
        file: File,
      }));
      setSnackbarType("fileInvalid");
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsDialogOpen(true);
  };

  const confirmSubmit = async () => {
    if (revokedEditions) {
      form.revoked_editions = revokedEditions
    }
    if (alteredEditions) {
      form.altered_editions = alteredEditions
    }
    setIsDialogOpen(false);
    if (!form.file.size || form.date.day === 0 || form.type.length === 0 || !form.code) {
      setSnackbarType("postInvalid");
      return;
    }
    const formData = new FormData();
    formData.append("file", form.file);
    formData.append("post_type", form.type.join(","));
    formData.append("date", formatDateFromObject(form.date));
    formData.append("hour", form.time);
    formData.append("number", form.code);
    formData.append("origin", form.origin);
    formData.append("url", form.url);
    formData.append("status", 'Vigente');
    formData.append("title", form.title);
    formData.append("revoked_editions", form.revoked_editions);
    formData.append("altered_editions", form.altered_editions);
    dispatch<any>(fetchPost(formData));
    setForm({
      file: File,
      date: {
        year: new Date().getFullYear(),
        month: new Date().getMonth() + 1,
        day: new Date().getUTCDate(),
      },
      type: [],
      status: [],
      code: "",
      time: "",
    });
  };

  const cancelSubmit = () => {
    setIsDialogOpen(false);
  };

  const handleSecondDialogOpen = () => {
    setIsSecondDialogOpen(true);
    dispatch<any>(fetchListPosts(page.toString(), true));
  };

  const handleThirdDialogOpen = () => {
    setIsThirdDialogOpen(true);
  };

  const handleThirdDialogSubmit = () => {
    confirmSubmit();
    setIsThirdDialogOpen(false);
    setIsSecondDialogOpen(false);
  };

  const handleSecondDialogClose = () => {
    setIsSecondDialogOpen(false);
    setSelectedAction(null);
    setAlteredEditions([]);
    setRevokedEditions([]);
  };

  const handleThirdDialogClose = () => {
    setIsThirdDialogOpen(false);
  };

  const handleConfirmSecondDialog = () => {
    if (form.title) {
      confirmSubmit();
      setIsSecondDialogOpen(false);
    }
    else {
      setIsThirdDialogOpen(true);
    }
  };

  const transformedData = getFiles?.data?.results?.map((item: any) => {
    const fileNameMatch = item.file_name.match(/name=(.*?)\./);
    const fileName = fileNameMatch ? fileNameMatch[1] : "Unknown File";
    const dateMatch = fileName.match(/date=(.*?)---/);
    const date = dateMatch ? dateMatch[1].replace(/-/g, "/") : "Unknown Date";
    const newFileNameMatch = item.file_name.match(/file=(.*?)\./);
    const newFileName = newFileNameMatch ? newFileNameMatch[1].replace("_", " ") : "Unknown File";
    const hourMatch = fileName.match(/hour=(.*?)---/);
    const hour = hourMatch ? hourMatch[1].replace(/-/g, "/") : "-";
    return {
      name: newFileName,
      date: date,
      hour: hour,
      presigned_url: item.presigned_url,
      delete: item.file_name.replace("files/", ""),
    };
  });

  useEffect(() => {
    dispatch<any>(
        fetchGetFiles(page.toString())
    );
    setIsDispatched(true);
  }, [dispatch, page, deleteFile?.data?.response]);

  useEffect(() => {
    dispatch<any>(
        fetchListPosts(page.toString(), true)
    );
    setIsDispatched(true);
  }, [dispatch, page]);

  useEffect(() => {
    const handleResize = () => {
      setIsResponsive(window.innerWidth <= 1800);
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleActionSelect = (action: string) => {
    if (action === 'revoga') {
      setAlteredSelected(false);
      setRevokeSelected(true);
    }
    if (action === 'altera') {
      setRevokeSelected(false);
      setAlteredSelected(true);
    }
    setSelectedAction(action);
  };

  const handleFileSelect = (fileName: string) => {
    if (selectedAction === 'revoga') {
      setRevokedEditions((prev: any) =>
          prev.includes(fileName) ? prev.filter((name: any) => name !== fileName) : [...prev, fileName]
      );
    } else if (selectedAction === 'altera') {
      setAlteredEditions((prev: any) =>
          prev.includes(fileName) ? prev.filter((name: any) => name !== fileName) : [...prev, fileName]
      );
    }
  };

  const today = dayjs();

  const handleDateChange = (newDate: Dayjs | null) => {
    const selectedDate = newDate ?? today;
    setForm((prev: any) => ({
      ...prev,
      date: {
        year: selectedDate.year(),
        month: selectedDate.month() + 1,
        day: selectedDate.date(),
      },
    }));
  };
  
  return (
      <div className={styles.container}>
        {getFiles.error && (
            <Snackbar type="scheduleError" setSnackbarType={setSnackbarType} />
        )}
        {post.error && (
            <Snackbar
                type={post.errorCode === "missing_description" ? "postMissingDescription" : "postError"}
                setSnackbarType={setSnackbarType}
            />
        )}
        {snackbarType === "fileInvalid" && (
            <Snackbar type="fileInvalid" setSnackbarType={setSnackbarType} />
        )}
        {snackbarType === "postInvalid" && (
            <Snackbar type="postInvalid" setSnackbarType={setSnackbarType} />
        )}
        <Dialog open={isDialogOpen} onClose={cancelSubmit} >
          <div id="dialog1">
            <DialogTitle>Atenção!</DialogTitle>
            <DialogContent>
              <p>Este ato altera ou revoga outros?</p>
            </DialogContent>
            <DialogActions>
              <Button className={`${styles.confirmButton}`} onClick={form.title ? confirmSubmit : handleThirdDialogOpen}>
                Não
              </Button>
              <Button className={`${styles.confirmButton}`} onClick={handleSecondDialogOpen} id="confirmButton">
                Sim
              </Button>
            </DialogActions>
          </div>
        </Dialog>
        <Dialog open={isThirdDialogOpen} onClose={cancelSubmit} >
          <div id="dialog3">
            <DialogTitle>Atenção!</DialogTitle>
            <DialogContent>
              <p>Nenhuma descrição foi adicionada para este ato. O sistema utilizará o primeiro parágrafo localizado abaixo do título para preenchimento automático do campo de descrição.</p>
            </DialogContent>
            <DialogActions>
              <Button className={`${styles.cancelButton}`} onClick={handleThirdDialogClose} id="confirmButton">
                Cancelar
              </Button>
              <Button className={`${styles.confirmButton3}`} onClick={handleThirdDialogSubmit}>
                Confirmar
              </Button>
            </DialogActions>
          </div>
        </Dialog>
        <Dialog open={isSecondDialogOpen} onClose={handleSecondDialogClose}>
          <DialogTitle>Selecione</DialogTitle>
          <DialogContent>
            {!selectedAction ? (
                <div className={styles.optionsButtonContainer}>
                  <div className={styles.optionsButton} id="dialog2">
                    <Button
                        className={`${styles.revokeButton}`}
                        onClick={() => handleActionSelect('revoga')}
                        >
                      Revoga
                    </Button>
                    <Button
                        className={`${styles.revokeButton}`}
                        onClick={() => handleActionSelect('altera')}
                        >
                      Altera
                    </Button>
                  </div>
                </div>
            ) : (
                <div className={styles.optionsButtonContainer}>
                  <div className={styles.optionsButton}>
                    <Button
                        className={`${styles.revokeButton} ${revokeSelected ? styles.selected : ''}`}
                        onClick={() => handleActionSelect('revoga')}
                    >
                      Revoga
                    </Button>
                    <Button
                        className={`${styles.revokeButton} ${alteredSelected ? styles.selected : ''}`}
                        onClick={() => handleActionSelect('altera')}
                    >
                      Altera
                    </Button>
                  </div>

                  <Autocomplete
                    multiple
                    options={Array.isArray(listPosts.data) ? listPosts.data.map((file: File) => ({
                      originalName: file.file_name, 
                      formattedName: formatFileName(file.file_name)
                    })) : []}
                    value={(alteredSelected ? alteredEditions : revokedEditions).map(name => ({
                      originalName: name,
                      formattedName: formatFileName(name)
                    }))}
                    onChange={(e: any, newValue: { originalName: string; formattedName: string }[]) => {
                      if (alteredSelected) {
                        setAlteredEditions(newValue.map(item => item.originalName));
                      } else {
                        setRevokedEditions(newValue.map(item => item.originalName));
                      }
                    }}
                    noOptionsText="Nenhum Ato encontrado"
                    renderInput={(params) => <TextField {...params} label="Ato alvo" />}
                    getOptionLabel={(option) => option.formattedName}
                    isOptionEqualToValue={(option, value) => option.originalName === value.originalName}
                    disableCloseOnSelect
                    renderOption={(props, option) => {
                      const isChecked = (alteredSelected ? alteredEditions : revokedEditions).includes(option.originalName);

                      return (
                        <li {...props} key={option.originalName} onClick={() => handleFileSelect(option.originalName)}>
                          <Checkbox checked={isChecked} />
                          {option.formattedName}
                        </li>
                      );
                    }}
                  />
                  <div className={styles.optionsButton}>
                    <Button
                        className={`${styles.cancelButton}`}
                        onClick={() => handleSecondDialogClose()}
                    >
                      Cancelar
                    </Button>
                    <Button
                        className={`${styles.confirmButton3}`}
                        onClick={() => handleConfirmSecondDialog()}
                    >
                      Confirmar
                    </Button>
                  </div>
                </div>
            )}
          </DialogContent>
        </Dialog>
        <div className={styles.postContainer} id="containerStatus">
          <div className={`${form.file ? styles.fileContainer : ""}`}>
            <div className={styles.uploadContainer}>
              <div className={styles.upload} id="fileButton">
                <label className={styles.fakeInput} htmlFor="file">
                  <MdUpload size={isResponsive ? 18 : 24} />
                  <div className={styles.uploadText}>{!form.file.size ? "Selecione o arquivo" : `${form.file.name}`}</div>
                </label>
                <Input
                    className={styles.file}
                    name="file"
                    type="file"
                    id="file"
                    onChange={handleFileChange}
                />
              </div>
              <div>
                {isResponsive ? (
                  <div
                    id="submitButton"
                    className={styles.buttonContainer}
                    onMouseEnter={() => {
                      if (!isFormValid()) setShowTooltip(true);
                    }}
                    onMouseLeave={() => setShowTooltip(false)}
                    onClick={() => setShowTooltip((prev) => !prev)}
                  >
                    <Button
                        className={isFormValid() ? `${styles.button} ${styles.schedule}` : `${styles.disabled}` }
                        onClick={handleSubmit}
                        disabled={!isFormValid()}
                    >
                      Agendar
                    </Button>
                    {!isFormValid() && showTooltip && (
                      <div className={styles.tooltip}>
                        Preencha todos os campos obrigatórios para agendar o ato!
                      </div>
                    )}
                </div>
                ) : null}
              </div>
            </div>
          </div>
          <div className={styles.allInputsContainer}>
            <div className={styles.firstColunmInputs}>
              <div className={styles.calendarContainer} id="scheduleButton">
                <LocalizationProvider 
                  dateAdapter={AdapterDayjs} 
                  adapterLocale="pt-br"
                  >
                  <div className={styles.calendar}>
                    <DatePicker
                      label="Data"
                      dayOfWeekFormatter={(weekday) => `${weekday.format('dd')}`}
                      value={dayjs(`${form.date.year}-${form.date.month}-${form.date.day}`)}
                      desktopModeMediaQuery="(min-width: 0px)"
                      onChange={(newValue) => handleDateChange(newValue)}
                      slotProps={{
                        textField: {
                          inputProps: {
                            placeholder: "DD/MM/AAAA",
                          },
                          sx: {
                            "&.MuiTextField-root": {
                              backgroundColor: "var(--second-button-color) ",
                              borderRadius: "5rem",
                              width: "100%",
                              boxShadow: "0 10px 10px -10px var(--shadow-color)",
                            },
                            "& .MuiInputBase-input": {
                              backgroundColor: "none !important",
                              boxShadow: "none",
                            },
                            "& .MuiOutlinedInput-root": {
                              height: "50px",
                              borderRadius: "5rem",
                              fontFamily: "var(--montserrat-font)",
                              backgroundColor: "none",
                              "& fieldset": {
                                border: "none",
                                borderColor: "none",
                              },

                            },
                            "& .MuiSvgIcon-root": {
                              color: "#4A4A4A",
                            },
                            "& .MuiInputLabel-root": {
                              fontFamily: "var(--montserrat-font)",
                              "&.Mui-focused": {
                                color: "#459436",
                              },
                            },
                          },
                        },
                        day: {
                          sx: {
                            "&.Mui-selected": {
                              backgroundColor: "var(--third-button-color) !important ",
                              "&:hover": {
                                backgroundColor: "var(--third-button-color-hover) !important",
                              },
                            },
                          }
                        }
                      }}
                    />
                  </div>
                </LocalizationProvider>
              </div>
              <div className={styles.type} id="typeButton">
                <SelectedList
                    className={styles.number}
                    placeholder="Tipo*"
                    field="type"
                    list={form}
                    setList={setForm}
                    options={optionsType}
                    isType
                    readOnly
                />
              </div>
              <div className={styles.type} id="typeButton">
                <SelectedList
                    className={styles.number}
                    placeholder="Origem*"
                    field="origin"
                    list={form}
                    setList={setForm}
                    options={optionsOrigin}
                    isType
                    readOnly
                />
              </div>
              <Input
                  className={styles.number}
                  placeholder="Número*"
                  name="code"
                  value={form.code}
                  onChange={handleChange}
                  max={4}
                  id="numberButton"
              />
            </div>
            <div className={styles.lastColumn} >
              <Input
                  className={styles.input}
                  placeholder="Descrição"
                  name="title"
                  value={form.title}
                  onChange={handleChange}
                  max={600}
                  id="titleButton"
              />
              <Input
                  className={styles.time}
                  placeholder="Horário"
                  name="time"
                  value={form.time}
                  onChange={handleTime}
                  id="timeButton"
              />
              <Input
                  className={styles.url}
                  placeholder="URL Diário Oficial"
                  name="url"
                  value={form.url}
                  onChange={handleChange}
                  id="urlButton"
              />
            </div>
          </div>
          <div className={styles.buttonContainer} >
            {!isResponsive ? (
              <div
              id="submitButton"
              className={styles.buttonContainer}
              onMouseEnter={() => {
                if (!isFormValid()) setShowTooltip(true);
              }}
              onMouseLeave={() => setShowTooltip(false)}
              onClick={() => setShowTooltip((prev) => !prev)}
            >
              <Button
                  className={isFormValid() ? `${styles.button} ${styles.schedule}` : `${styles.disabled}` }
                  onClick={handleSubmit}
                  disabled={!isFormValid()}
              >
                Agendar
              </Button>
              {!isFormValid() && showTooltip && (
                <div className={styles.tooltipBiggerScreen}>
                  Preencha todos os campos obrigatórios para agendar o ato!
                </div>
              )}
          </div>
            ) : null}
          </div>
        </div>
        <div className={styles.table}>
          <Table
              title="Atos Agendados"
              columns={columns}
              data={transformedData}
              setPage={setPage}
              page={page}
              total={getFiles.data.count}
              downloadButton
              isEmpty={isDispatched && getFiles?.data?.results?.length === 0}
              isStatus
              loading={post.loading || getFiles.loading}
              error={post.error || getFiles.error}
          />
        </div>
      </div>
  );
};

export default Status;
